import { ReactNode } from 'react'

import { MetricData } from '../../types/insights.types'
import { InsightsColourMap } from '../../utils/colourResolver'
import {
  buildTrendTooltip,
  formatTrend,
  formatValueHero,
} from '../../utils/formatValue'
import { mapLegendItems } from '../../utils/mapBuckets'
import {
  axisAwareLegendItems,
  legendItems,
  rowAxisLabel,
  seriesTotalsLegendItems,
} from '../../utils/metricView'
import { MetricRenderer } from '../MetricView/MetricRenderer'
import {
  ChartSpec,
  MetricView,
  MetricViewProps,
} from '../MetricView/MetricView'
import {
  AreaChartBody,
  BarChartBody,
  DonutChartBody,
  LineChartBody,
  TableChartBody,
  TableLabels,
} from './chartBodies'
import { MapChartBody } from './MapChartBody'

// Each chart type explicitly declares what it supports, its legend, and its body.
// The rules live with the chart, so there's no central switch — only AutoChart
// dispatches on the data shape, which is its whole job.

const isBreakdown = (data: MetricData) =>
  data.type === 'rows' || data.type === 'seriesRows'

// A line chart's domain: a breakdown whose rows are time buckets (one or many series).
const isTimeseries = (data: MetricData) =>
  (data.type === 'rows' || data.type === 'seriesRows') &&
  data.structure === 'timeseries'

const barSpec: ChartSpec = {
  supports: isBreakdown,
  // A stacked bar's legend is its series; a single-series bar relies on the axis. A
  // mixed-unit breakdown renders as grouped bars across two axes, so the legend
  // also marks any series the split couldn't place.
  legend: (data, colourMap, options) =>
    data.type === 'seriesRows'
      ? axisAwareLegendItems(data, colourMap, options)
      : [],
  renderBody: (props) => <BarChartBody {...props} />,
}

// The same bar body laid on its side — for a breakdown whose row labels are long or
// numerous, where rotated category ticks are unreadable. An explicit `orientation`
// prop still wins; the spec only supplies the default.
const barHorizontalSpec: ChartSpec = {
  ...barSpec,
  renderBody: (props) => (
    <BarChartBody {...props} orientation={props.orientation ?? 'horizontal'} />
  ),
}

const donutSpec: ChartSpec = {
  supports: isBreakdown,
  // Slices are rows (or a series breakdown's row totals).
  legend: (data, colourMap) =>
    data.type === 'seriesRows'
      ? seriesTotalsLegendItems(data, colourMap)
      : data.type === 'rows'
      ? legendItems(data, colourMap)
      : [],
  // A donut has no axes, so its legend is the only place the rows' dimension can be
  // named — and every entry is a row, whichever breakdown shape it came from.
  legendHeading: rowAxisLabel,
  renderBody: (props) => <DonutChartBody {...props} />,
}

const statSpec: ChartSpec = {
  supports: (data) => data.type === 'scalar',
  legend: () => [],
  renderBody: () => null,
}

// A multi-series (seriesRows) timeseries carries a legend; a single-series (flat
// rows) one relies on its axis label. Series the dual-axis split couldn't plot are
// marked rather than dropped.
const timeseriesLegend: ChartSpec['legend'] = (data, colourMap, options) =>
  data.type === 'seriesRows'
    ? axisAwareLegendItems(data, colourMap, options)
    : []

const lineSpec: ChartSpec = {
  supports: isTimeseries,
  legend: timeseriesLegend,
  renderBody: (props) => <LineChartBody {...props} />,
}

const areaSpec: ChartSpec = {
  supports: isTimeseries,
  legend: timeseriesLegend,
  renderBody: (props) => <AreaChartBody {...props} />,
}

const stackedAreaSpec: ChartSpec = {
  supports: isTimeseries,
  legend: timeseriesLegend,
  renderBody: (props) => <AreaChartBody {...props} stacked />,
}

// A table renders any breakdown (flat rows or series-split); the table columns
// carry the labelling, so it needs no legend.
const tableSpec: ChartSpec = {
  supports: isBreakdown,
  legend: () => [],
  renderBody: (props) => <TableChartBody {...props} />,
}

const autoSpec: ChartSpec = {
  supports: () => true,
  legend: (data, colourMap, options) => {
    // Timeseries: only a multi-series one carries a legend, and it renders on two
    // axes, so it needs the axis-aware legend.
    if (isTimeseries(data)) return timeseriesLegend(data, colourMap, options)
    if (data.type === 'seriesRows')
      return axisAwareLegendItems(data, colourMap, options)
    if (data.type === 'rows') return legendItems(data, colourMap)

    return []
  },
  // Only the auto pick's donut branch (a flat `rows` breakdown) lists rows; a bar or
  // line lists series, which the row axis would misname.
  legendHeading: (data) =>
    data.type === 'rows' && !isTimeseries(data)
      ? rowAxisLabel(data)
      : undefined,
  renderBody: (props) => {
    const { data } = props

    if (data.type === 'scalar') return null
    if (isTimeseries(data)) return <LineChartBody {...props} />
    if (data.type === 'seriesRows') return <BarChartBody {...props} />

    return <DonutChartBody {...props} />
  },
}

/**
 * A backend-supplied hint for how to render a metric result (e.g. the model
 * answering an ad-hoc insights-ai question suggests a stacked area for a
 * part-to-whole-over-time breakdown). Advisory only: {@link specForSuggestedChart}
 * validates it against the actual data shape and falls back to the auto pick when
 * the hint is unknown or doesn't fit.
 */
export type SuggestedChart =
  | 'stat'
  | 'bar'
  | 'stackedBar'
  | 'barHorizontal'
  | 'donut'
  | 'line'
  | 'area'
  | 'stackedArea'
  | 'table'

// `bar`/`stackedBar` both map to barSpec (BarChartBody already stacks a seriesRows
// and renders a single-series rows flat); the hint just confirms the intent.
// `barHorizontal` is the one bar hint that changes the render — it flips the
// orientation.
const suggestedChartSpecs: Record<SuggestedChart, ChartSpec> = {
  stat: statSpec,
  bar: barSpec,
  stackedBar: barSpec,
  barHorizontal: barHorizontalSpec,
  donut: donutSpec,
  line: lineSpec,
  area: areaSpec,
  stackedArea: stackedAreaSpec,
  table: tableSpec,
}

/**
 * Resolves a render hint (untrusted — it originates from the model, over the wire)
 * to a chart spec, guarding against a hint the data can't satisfy (e.g. `stackedArea`
 * for a non-timeseries breakdown) or an unrecognised string: both fall back to
 * `autoSpec` so the chart always renders something sane.
 */
export function specForSuggestedChart(
  suggested: string | undefined,
  data: MetricData
): ChartSpec {
  const spec = suggested
    ? (suggestedChartSpecs as Record<string, ChartSpec | undefined>)[suggested]
    : undefined

  return spec && spec.supports(data) ? spec : autoSpec
}

/** A stacked (seriesRows) or single-series (rows) bar chart. */
export type InsightsBarChartProps = Omit<MetricViewProps, 'innerRadiusRatio'>

export function InsightsBarChart(props: Readonly<InsightsBarChartProps>) {
  return <MetricView {...props} spec={barSpec} />
}

/** A donut chart of a flat breakdown (rows) or a series breakdown's row totals. */
export type InsightsDonutChartProps = Omit<
  MetricViewProps,
  'orientation' | 'wrapXAxisLabels' | 'yAxisWidth'
>

export function InsightsDonutChart(props: Readonly<InsightsDonutChartProps>) {
  return <MetricView {...props} spec={donutSpec} />
}

/**
 * A timeseries line chart. Series default to the left axis; `rightAxisSeries`
 * (or, when omitted, a differing value type) moves a series to a secondary
 * right axis — e.g. donation count on the left, donation amount (£) on the right.
 */
export type InsightsLineChartProps = Omit<
  MetricViewProps,
  'orientation' | 'wrapXAxisLabels' | 'yAxisWidth' | 'innerRadiusRatio'
>

export function InsightsLineChart(props: Readonly<InsightsLineChartProps>) {
  return <MetricView {...props} spec={lineSpec} />
}

/**
 * A timeseries area chart. `stacked` cumulates the series into part-to-whole
 * bands (all pinned to one axis); otherwise the series are overlaid translucently.
 */
export type InsightsAreaChartProps = Omit<
  MetricViewProps,
  'orientation' | 'wrapXAxisLabels' | 'yAxisWidth' | 'innerRadiusRatio'
> & { stacked?: boolean }

export function InsightsAreaChart({
  stacked,
  ...props
}: Readonly<InsightsAreaChartProps>) {
  return <MetricView {...props} spec={stacked ? stackedAreaSpec : areaSpec} />
}

export type InsightsMapProps = Pick<
  MetricViewProps,
  | 'metric'
  | 'filters'
  | 'title'
  | 'subtitle'
  | 'emptyState'
  | 'errorLabel'
  | 'testId'
> & {
  legendLabel?: string
  /** Names the value in the country hover tooltip, e.g. "Total granted". */
  valueLabel?: string
}

export function InsightsMap({
  legendLabel,
  valueLabel,
  ...props
}: Readonly<InsightsMapProps>) {
  const spec: ChartSpec = {
    supports: (data) => data.type === 'rows' && !isTimeseries(data),
    legend: (data, _colourMap, { locale }) =>
      data.type === 'rows' ? mapLegendItems(data, locale) : [],
    legendHeading: () => legendLabel,
    renderBody: (bodyProps) => (
      <MapChartBody {...bodyProps} valueLabel={valueLabel} />
    ),
  }

  return <MetricView {...props} spec={spec} />
}

/** Picks the chart from the data shape (seriesRows → bar, rows → donut, timeseries → line). */
export type InsightsAutoChartProps = MetricViewProps

export function InsightsAutoChart(props: Readonly<InsightsAutoChartProps>) {
  return <MetricView {...props} spec={autoSpec} />
}

/**
 * A headline scalar metric (value + trend + title), no chart body.
 * `seriesLabels`/`rowLabels` are intentionally omitted — a scalar has no
 * breakdown rows or series to re-label.
 */
export type InsightsStatProps = Pick<
  MetricViewProps,
  | 'metric'
  | 'filters'
  | 'limit'
  | 'title'
  | 'subtitle'
  | 'description'
  | 'trendTooltip'
  | 'errorLabel'
  | 'height'
  | 'testId'
>

export function InsightsStat(props: Readonly<InsightsStatProps>) {
  return <MetricView {...props} spec={statSpec} showLegend={false} />
}

/**
 * Renders a `MetricData` you already hold (e.g. an ad-hoc insights-ai answer) rather than a
 * metric slug, auto-picking the chart from the data shape. This is the data-only counterpart to
 * `Insights.AutoChart` (which fetches by slug): same native rendering, no fetch. Renders outside
 * `Insights.Domain`, so colours / series order come from props.
 */
export type InsightsMetricChartProps = {
  data: MetricData
  /** StatCard heading (a scalar's value + trend render beneath it; a breakdown's chart does). */
  title?: string
  subtitle?: string
  description?: string
  colourMap?: InsightsColourMap
  seriesOrder?: string[]
  /**
   * Advisory render hint (e.g. from an insights-ai answer). Validated against the
   * data shape — an unknown or ill-fitting hint falls back to the auto pick.
   */
  suggestedChart?: SuggestedChart
  /** BCP 47 locale for the hero value + trend formatting; defaults to `en-US`. */
  locale?: string
  /**
   * Comparison-window copy for the trend tooltip (the prior value is always appended). Defaults
   * to the trend's own backend date range; pass localised copy like "vs the previous 30 days".
   */
  trendTooltip?: string
  showLegend?: boolean
  height?: number
  orientation?: 'vertical' | 'horizontal'
  wrapXAxisLabels?: boolean
  yAxisWidth?: number
  innerRadiusRatio?: number
  rightAxisSeries?: string[]
  /** Translated headers for the `table` body's generic Value/Total/Share columns. */
  tableLabels?: TableLabels
  /**
   * Pre-translated hover copy for a legend entry the chart couldn't plot, i.e. a
   * breakdown carrying three-plus units — two axes can't scale them all, so the
   * remaining series are named in the legend but left off the chart.
   */
  notShownTooltip?: string
  emptyState?: ReactNode
  errorLabel?: string
  testId?: string
}

export function InsightsMetricChart({
  data,
  title = '',
  subtitle,
  description,
  colourMap = {},
  seriesOrder = [],
  suggestedChart,
  locale,
  trendTooltip,
  showLegend = true,
  height,
  orientation,
  wrapXAxisLabels,
  yAxisWidth,
  innerRadiusRatio,
  rightAxisSeries,
  tableLabels,
  notShownTooltip,
  emptyState,
  errorLabel,
  testId = 'insights-metric-chart',
}: Readonly<InsightsMetricChartProps>) {
  const scalar = data.type === 'scalar' ? data : undefined
  const value = scalar ? formatValueHero(scalar.value, locale) : undefined
  const trendData = formatTrend(scalar?.trend, locale)
  const trend = trendData
    ? {
        value: trendData.value,
        direction: trendData.direction,
        tooltip: buildTrendTooltip(trendData, trendTooltip),
      }
    : undefined

  return (
    <MetricRenderer
      data={data}
      spec={specForSuggestedChart(suggestedChart, data)}
      colourMap={colourMap}
      seriesOrder={seriesOrder}
      label={title}
      value={value}
      subtitle={subtitle}
      description={description}
      trend={trend}
      showLegend={showLegend}
      height={height}
      locale={locale}
      orientation={orientation}
      wrapXAxisLabels={wrapXAxisLabels}
      yAxisWidth={yAxisWidth}
      innerRadiusRatio={innerRadiusRatio}
      rightAxisSeries={rightAxisSeries}
      tableLabels={tableLabels}
      notShownTooltip={notShownTooltip}
      emptyState={emptyState}
      errorLabel={errorLabel}
      testId={testId}
    />
  )
}
